/**
 * FlowTime — хранение данных таймера производительности.
 *
 * Всё лежит в localStorage этого браузера: сессии фокуса, задачи, проекты
 * и настройки. Сервера у инструмента нет намеренно — учёт рабочего времени
 * это личные данные, и отправлять их куда-либо ради подсчёта минут незачем.
 *
 * Ключ версионирован: при смене формата старая запись не ломает приложение,
 * а проходит через `migrate`.
 */

export type TimerMode = 'focus' | 'short' | 'long' | 'free';

export interface FlowProject {
  id: string;
  name: string;
  color: string;
  createdAt: number;
}

export interface FlowTask {
  id: string;
  title: string;
  projectId: string | null;
  /** Оценка в помидорах. 0 означает «без оценки». */
  estimate: number;
  done: boolean;
  doneAt: number | null;
  createdAt: number;
  /** Позиция в списке: меньше — выше. */
  order: number;
  notes: string;
}

export interface FlowEntry {
  id: string;
  start: number;
  end: number;
  mode: TimerMode;
  taskId: string | null;
  projectId: string | null;
  note: string;
  /** Сколько раз человек отметил, что отвлёкся. */
  interruptions: number;
  /** Доведена до конца или прервана досрочно. */
  completed: boolean;
  /** Добавлена вручную задним числом. */
  manual: boolean;
}

/** Живая сессия: переживает перезагрузку страницы. */
export interface FlowSession {
  mode: TimerMode;
  taskId: string | null;
  projectId: string | null;
  startedAt: number | null;
  plannedMs: number;
  /** Накопленное время без учёта текущего отрезка. */
  elapsedMs: number;
  running: boolean;
  /** Момент последнего запуска, по стенным часам. */
  lastResume: number | null;
  interruptions: number;
  note: string;
}

export interface FlowSettings {
  focusMin: number;
  shortMin: number;
  longMin: number;
  /** Длинный перерыв каждые N помидоров. */
  longEvery: number;
  goalHours: number;
  autoStartBreak: boolean;
  autoStartFocus: boolean;
  /** Строгий режим: паузы запрещены, только сброс. */
  strict: boolean;
  titleCountdown: boolean;
  logBreaks: boolean;
  alarm: 'bell' | 'chime' | 'digital' | 'wood' | 'none';
  volume: number;
  ambient: 'none' | 'tick' | 'brown' | 'rain';
  notify: boolean;
  weekStart: 0 | 1;
}

export interface FlowData {
  schema: number;
  createdAt: number;
  settings: FlowSettings;
  projects: FlowProject[];
  tasks: FlowTask[];
  entries: FlowEntry[];
  session: FlowSession | null;
  /** Сколько помидоров пройдено в текущем цикле до длинного перерыва. */
  cyclePos: number;
}

export const FLOW_KEY = 'dh.flowtime.v1';
const SCHEMA = 1;

export const DEFAULT_SETTINGS: FlowSettings = {
  focusMin: 25,
  shortMin: 5,
  longMin: 15,
  longEvery: 4,
  goalHours: 4,
  autoStartBreak: true,
  autoStartFocus: false,
  strict: false,
  titleCountdown: true,
  logBreaks: false,
  alarm: 'bell',
  volume: 0.7,
  ambient: 'none',
  notify: false,
  weekStart: 1,
};

/**
 * Палитра проектов.
 *
 * Цвета яркие намеренно: тема DataHunter нейтральная, серо-чёрная, и
 * единственный цвет в интерфейсе — это метка проекта. По ней взгляд и
 * находит нужную строку в журнале.
 */
export const FLOW_PALETTE = [
  '#e8e8ea',
  '#f59e0b',
  '#22c55e',
  '#38bdf8',
  '#a78bfa',
  '#f472b6',
  '#22d3ee',
  '#84cc16',
  '#fb923c',
  '#94a3b8',
];

export function flowUid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 9);
}

export function emptyData(): FlowData {
  return {
    schema: SCHEMA,
    createdAt: Date.now(),
    settings: { ...DEFAULT_SETTINGS },
    projects: [],
    tasks: [],
    entries: [],
    session: null,
    cyclePos: 0,
  };
}

/**
 * Приведение записи к текущему формату.
 *
 * Данные читаются из localStorage, то есть из источника, который мог
 * править кто угодно и который пережил прошлые версии приложения. Поэтому
 * поля разбираются как `unknown` и проверяются по одному, а мусорные
 * записи отбрасываются: одна битая строка не должна ронять весь журнал.
 */
export function migrate(raw: unknown): FlowData {
  const base = emptyData();
  if (!raw || typeof raw !== 'object') return base;
  const d = raw as Record<string, unknown>;

  const rows = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value)
      ? value.filter((v): v is Record<string, unknown> => !!v && typeof v === 'object')
      : [];

  const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
  const numOr = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) ? v : fallback;

  const projects: FlowProject[] = rows(d.projects)
    .filter((p) => typeof p.id === 'string')
    .map((p, i) => ({
      id: str(p.id),
      // Имя без подстановки на языке интерфейса: хранилище не знает
      // локали, а безымянный проект в списке виден и так.
      name: str(p.name).slice(0, 60),
      color: str(p.color) || (FLOW_PALETTE[i % FLOW_PALETTE.length] ?? '#94a3b8'),
      createdAt: numOr(p.createdAt, Date.now()),
    }));

  const tasks: FlowTask[] = rows(d.tasks)
    .filter((t) => typeof t.id === 'string')
    .map((t, i) => ({
      id: str(t.id),
      title: str(t.title).slice(0, 200),
      projectId: typeof t.projectId === 'string' ? t.projectId : null,
      estimate: Math.max(0, Math.min(99, numOr(t.estimate, 1))),
      done: t.done === true,
      doneAt: typeof t.doneAt === 'number' ? t.doneAt : null,
      createdAt: numOr(t.createdAt, Date.now()),
      order: numOr(t.order, i),
      notes: str(t.notes).slice(0, 2000),
    }));

  const MODES: TimerMode[] = ['focus', 'short', 'long', 'free'];

  const entries: FlowEntry[] = rows(d.entries)
    .filter((e) => typeof e.start === 'number' && typeof e.end === 'number' && e.end > e.start)
    .map((e) => ({
      id: str(e.id) || flowUid(),
      start: numOr(e.start, 0),
      end: numOr(e.end, 0),
      mode: MODES.includes(e.mode as TimerMode) ? (e.mode as TimerMode) : 'focus',
      taskId: typeof e.taskId === 'string' ? e.taskId : null,
      projectId: typeof e.projectId === 'string' ? e.projectId : null,
      note: str(e.note).slice(0, 500),
      interruptions: Math.max(0, numOr(e.interruptions, 0)),
      // Записи прошлых версий поля не имели: отсутствие означает «дошла
      // до конца», иначе весь старый журнал стал бы «прерванным».
      completed: e.completed !== false,
      manual: e.manual === true,
    }))
    .sort((a, b) => a.start - b.start);

  const rawSettings =
    d.settings && typeof d.settings === 'object' ? (d.settings as Record<string, unknown>) : {};

  /**
   * Числовые настройки чистятся по одной.
   *
   * Раньше объект просто накладывался поверх умолчаний, и любое
   * нечисловое значение доезжало до таймера: при `focusMin: "много"`
   * на циферблате горело «NaN:NaN», а `longEvery: 0` давал деление на
   * ноль в счётчике цикла. Файл хранилища переживает обновления и
   * правки руками, поэтому доверять ему нельзя.
   */
  const clampNum = (key: string, fallback: number, min: number, max: number): number => {
    const v = rawSettings[key];
    if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
    return Math.min(max, Math.max(min, v));
  };

  /**
   * Булевы поля берутся строго по типу, а не «на истинность».
   *
   * Строка «нет» в JavaScript истинна, и при `strict: "нет"` строгий
   * режим включался сам собой: кнопка паузы блокировалась, и человек
   * оказывался заперт в интервале без возможности остановить таймер.
   */
  const bool = (key: string, fallback: boolean): boolean => {
    const v = rawSettings[key];
    return typeof v === 'boolean' ? v : fallback;
  };

  /** Списковые поля: чужое значение молча заменяется умолчанием. */
  const oneOf = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
    const v = rawSettings[key];
    return typeof v === 'string' && (allowed as readonly string[]).includes(v)
      ? (v as T)
      : fallback;
  };

  const settings: FlowSettings = {
    ...base.settings,
    focusMin: clampNum('focusMin', base.settings.focusMin, 1, 600),
    shortMin: clampNum('shortMin', base.settings.shortMin, 1, 120),
    longMin: clampNum('longMin', base.settings.longMin, 1, 240),
    longEvery: clampNum('longEvery', base.settings.longEvery, 1, 12),
    goalHours: clampNum('goalHours', base.settings.goalHours, 0, 24),
    volume: clampNum('volume', base.settings.volume, 0, 1),
    autoStartBreak: bool('autoStartBreak', base.settings.autoStartBreak),
    autoStartFocus: bool('autoStartFocus', base.settings.autoStartFocus),
    strict: bool('strict', base.settings.strict),
    titleCountdown: bool('titleCountdown', base.settings.titleCountdown),
    logBreaks: bool('logBreaks', base.settings.logBreaks),
    notify: bool('notify', base.settings.notify),
    alarm: oneOf(
      'alarm',
      ['bell', 'chime', 'digital', 'wood', 'none'] as const,
      base.settings.alarm,
    ),
    ambient: oneOf('ambient', ['none', 'tick', 'brown', 'rain'] as const, base.settings.ambient),
    // Неделя начинается либо с воскресенья, либо с понедельника.
    weekStart: rawSettings.weekStart === 0 ? 0 : 1,
  };

  /**
   * Сессия тоже проверяется, а не берётся как есть.
   *
   * Битые `plannedMs` или `elapsedMs` дают на циферблате «NaN:NaN», а
   * неизвестный режим ломает выбор длительности. Испорченная сессия
   * просто отбрасывается: потеря одного незавершённого интервала
   * несравнима с неработающим таймером.
   */
  const rawSession = d.session;
  let session: FlowSession | null = null;
  if (rawSession && typeof rawSession === 'object') {
    const s = rawSession as Record<string, unknown>;
    const mode = MODES.includes(s.mode as TimerMode) ? (s.mode as TimerMode) : null;
    const plannedMs = numOr(s.plannedMs, NaN);
    const elapsedMs = numOr(s.elapsedMs, NaN);
    if (mode && Number.isFinite(plannedMs) && Number.isFinite(elapsedMs)) {
      session = {
        mode,
        taskId: typeof s.taskId === 'string' ? s.taskId : null,
        projectId: typeof s.projectId === 'string' ? s.projectId : null,
        startedAt: typeof s.startedAt === 'number' ? s.startedAt : null,
        plannedMs: Math.max(0, plannedMs),
        elapsedMs: Math.max(0, elapsedMs),
        running: s.running === true,
        lastResume: typeof s.lastResume === 'number' ? s.lastResume : null,
        interruptions: Math.max(0, numOr(s.interruptions, 0)),
        note: str(s.note).slice(0, 500),
      };
    }
  }

  return {
    schema: SCHEMA,
    createdAt: numOr(d.createdAt, Date.now()),
    settings,
    projects,
    tasks,
    entries,
    session,
    cyclePos: Math.max(0, numOr(d.cyclePos, 0)),
  };
}

export function loadFlowData(): { data: FlowData; recovered: boolean } {
  if (typeof window === 'undefined') return { data: emptyData(), recovered: false };
  try {
    const raw = window.localStorage.getItem(FLOW_KEY);
    if (!raw) return { data: emptyData(), recovered: false };
    return { data: migrate(JSON.parse(raw)), recovered: false };
  } catch {
    // Разбор не удался: сохраняем копию под другим ключом, чтобы человек
    // мог достать данные руками, и начинаем с чистого листа.
    try {
      const broken = window.localStorage.getItem(FLOW_KEY);
      if (broken) window.localStorage.setItem(`${FLOW_KEY}.broken.${String(Date.now())}`, broken);
    } catch {
      /* места нет — тогда и копию сделать негде */
    }
    return { data: emptyData(), recovered: true };
  }
}

/** Возвращает false, если браузеру не хватило места. */
export function saveFlowData(data: FlowData): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(FLOW_KEY, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
}

export function storageBytes(): number {
  if (typeof window === 'undefined') return 0;
  try {
    return new Blob([window.localStorage.getItem(FLOW_KEY) ?? '']).size;
  } catch {
    return 0;
  }
}
